import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";

import {
  compareDatatableStates,
  type DatatableState,
  filterDatatableStateByColumns,
} from "@/domain/view/models/datatable-state-model";
import {
  ColumnDef,
  ColumnFiltersState,
  ColumnOrderState,
  ExpandedState,
  getCoreRowModel,
  getExpandedRowModel,
  getFilteredRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  PaginationState,
  Row,
  SortingState,
  useReactTable,
  VisibilityState,
} from "@tanstack/react-table";

import { createDatatableLock } from "../datatable-lock";
import {
  type DatatableColumn,
  DatatableRowData,
  groupRowsBySection,
  isSectionRow,
  isSubSectionRow,
  SubSectionData,
  TSectionData,
} from "../datatable-types";

import { DatatableContext, type DatatableContextType } from "./datatable-context";
import { type InternalTableStateProviderProps } from "./datatable-context-props";
import { useDebouncedStateSave } from "./use-debounced-state-save";

const DEFAULT_PAGE_SIZE = 20;

const getSubRowList = <TData extends DatatableRowData>(
  row: TSectionData<TData>,
): TData[] | SubSectionData<TData>[] | undefined => {
  if (isSectionRow(row) || isSubSectionRow(row)) {
    return row.data;
  }
  return undefined;
};

export const InternalDataTableContextProvider = <TData extends DatatableRowData>({
  data,
  children,
  debugTable = false,

  globalFilterFn,
  getColumnCanGlobalFilter,

  initialState,
  saveDatatableState,
  saveDebounceMs,
  initWithUrlParams,
  pageSize,
  lockPublishedConfiguration = false,

  ...props
}: InternalTableStateProviderProps<TData>) => {
  const isPaginationEnabled = pageSize !== undefined;
  const urlSyncMountedRef = useRef(false);
  const skipNextSaveRef = useRef(false);
  const [, setSearchParams] = useSearchParams();

  const columnIdSignature = props.columns.map((column) => column.columnId).join(" ");
  const columnCacheRef = useRef<{ signature: string; columnList: DatatableColumn<TData>[] }>({
    signature: columnIdSignature,
    columnList: props.columns as DatatableColumn<TData>[],
  });
  if (columnCacheRef.current.signature !== columnIdSignature) {
    columnCacheRef.current = {
      signature: columnIdSignature,
      columnList: props.columns as DatatableColumn<TData>[],
    };
  }
  const columns = columnCacheRef.current.columnList;

  const columnByColumnId = useMemo(() => {
    return new Map(columns.map((column) => [column.columnId, column]));
  }, [columns]);

  const validColumnIdSet = useMemo<Set<string>>(() => {
    return new Set<string>(columnByColumnId.keys());
  }, [columnByColumnId]);

  const getConfigurationForColumn = useCallback(
    (columnId: string) => columnByColumnId.get(columnId),
    [columnByColumnId],
  );

  const [showColumnHeaders, setShowColumnHeaders] = useState(initialState.showColumnHeaders);
  const [sectionColumnId, setSectionColumnId] = useState<string | null | undefined>(initialState.sectionColumnId);
  const [subSectionColumnId, setSubSectionColumnId] = useState<string | null | undefined>(
    initialState.subSectionColumnId,
  );
  const [globalFilter, setGlobalFilter] = useState<string>(initialState.globalFilter);
  const [expanded, setExpanded] = useState<ExpandedState>(initialState.expanded);
  const [columnFilters, setColumnFilters] = useState<ColumnFiltersState>(initialState.columnFilters);
  const [columnOrder, setColumnOrder] = useState<ColumnOrderState>(initialState.columnOrder);
  const [columnVisibility, setColumnVisibility] = useState<VisibilityState>(initialState.columnVisibility);
  const [sorting, setSorting] = useState<SortingState>(initialState.sorting);
  const [mountedColumnFilterList] = useState<ColumnFiltersState>(initialState.columnFilters);

  const [pagination, setPagination] = useState<PaginationState>({
    pageIndex: 0,
    pageSize: pageSize ?? DEFAULT_PAGE_SIZE,
  });

  const localState = useMemo(() => {
    return {
      showColumnHeaders,
      sectionColumnId,
      subSectionColumnId,
      columnFilters,
      globalFilter,
      columnOrder,
      columnVisibility,
      sorting,
      expanded,
    };
  }, [
    showColumnHeaders,
    sectionColumnId,
    subSectionColumnId,
    columnFilters,
    globalFilter,
    columnOrder,
    columnVisibility,
    sorting,
    expanded,
  ]);

  // Pruning the saved state at its source would drop entries for label columns that load later.
  const appliedState = useMemo<DatatableState>(() => {
    return filterDatatableStateByColumns(localState, validColumnIdSet);
  }, [localState, validColumnIdSet]);

  const tableData = useMemo(() => {
    if (appliedState.sectionColumnId) {
      return groupRowsBySection(data, columns, appliedState.sectionColumnId, appliedState.subSectionColumnId);
    }
    return data;
  }, [data, columns, appliedState.sectionColumnId, appliedState.subSectionColumnId]);

  const tableConfig = useMemo(() => {
    return {
      debugTable,
      data: tableData,
      columns: columns.map((column) => column.getColumnDef()) as ColumnDef<TSectionData<TData>, unknown>[],
      state: {
        columnFilters: appliedState.columnFilters,
        globalFilter: appliedState.globalFilter,
        columnOrder: appliedState.columnOrder,
        columnVisibility: appliedState.columnVisibility,
        sorting: appliedState.sorting,
        expanded: appliedState.expanded,
        ...(isPaginationEnabled && { pagination }),
      },

      filterFromLeafRows: true,
      getCoreRowModel: getCoreRowModel(),
      getSortedRowModel: getSortedRowModel(),
      getFilteredRowModel: getFilteredRowModel(),
      getExpandedRowModel: getExpandedRowModel(),
      ...(isPaginationEnabled && { getPaginationRowModel: getPaginationRowModel() }),

      onGlobalFilterChange: setGlobalFilter,
      onColumnFiltersChange: setColumnFilters,
      onColumnOrderChange: setColumnOrder,
      onColumnVisibilityChange: setColumnVisibility,
      onSortingChange: setSorting,
      onExpandedChange: setExpanded,
      ...(isPaginationEnabled && { onPaginationChange: setPagination }),

      globalFilterFn,
      getColumnCanGlobalFilter,

      getRowCanExpand: (row: Row<TSectionData<TData>>) => (getSubRowList(row.original)?.length ?? 0) > 0,
      getSubRows: getSubRowList,

      defaultColumn: {
        minSize: 0,
        size: Number.MAX_SAFE_INTEGER,
        maxSize: Number.MAX_SAFE_INTEGER,
      },
    };
  }, [
    debugTable,
    tableData,
    columns,
    globalFilterFn,
    getColumnCanGlobalFilter,
    appliedState,
    pagination,
    isPaginationEnabled,
  ]);

  const table = useReactTable<TSectionData<TData>>(tableConfig);

  useDebouncedStateSave({ state: localState, saveDatatableState, skipNextSaveRef, debounceMs: saveDebounceMs });

  useEffect(() => {
    if (!initWithUrlParams) return;

    if (!urlSyncMountedRef.current) {
      urlSyncMountedRef.current = true;
      return;
    }

    setSearchParams(
      (prev) => {
        const next = new URLSearchParams(prev);
        columns.forEach((column) => {
          next.delete(`f_${column.columnId}`);
          column.urlParamListToClean?.forEach((param) => next.delete(param));
        });
        columnFilters.forEach((filter) => {
          next.set(`f_${filter.id}`, encodeURIComponent(JSON.stringify(filter.value)));
        });
        return next;
      },
      { replace: true },
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columnFilters]);

  const setState = ({
    state: newState,
    skipApiPropagation = false,
  }: {
    state: Partial<DatatableState>;
    skipApiPropagation?: boolean;
  }) => {
    if (compareDatatableStates({ ...localState, ...newState }, localState)) {
      return;
    }

    if (skipApiPropagation) {
      skipNextSaveRef.current = true;
    }

    let nextSectionColumnId = localState.sectionColumnId;
    if (newState.sectionColumnId !== undefined) {
      setSectionColumnId(newState.sectionColumnId);
      nextSectionColumnId = newState.sectionColumnId;

      if (newState.sectionColumnId === localState.subSectionColumnId || newState.sectionColumnId === null) {
        setSubSectionColumnId(null);
      }
    }

    if (nextSectionColumnId && newState.subSectionColumnId !== undefined) {
      setSubSectionColumnId(newState.subSectionColumnId);
    }

    if (newState.showColumnHeaders !== undefined) {
      setShowColumnHeaders(newState.showColumnHeaders);
    }
    if (newState.columnFilters !== undefined) {
      setColumnFilters(newState.columnFilters);
    }
    if (newState.globalFilter !== undefined) {
      setGlobalFilter(newState.globalFilter);
    }
    if (newState.columnOrder !== undefined) {
      setColumnOrder(newState.columnOrder);
    }
    if (newState.columnVisibility !== undefined) {
      setColumnVisibility(newState.columnVisibility);
    }
    if (newState.sorting !== undefined) {
      setSorting(newState.sorting);
    }
    if (newState.expanded !== undefined) {
      setExpanded(newState.expanded);
    }
  };

  const lock = useMemo(() => {
    return createDatatableLock({ isLocked: lockPublishedConfiguration, mountedColumnFilterList });
  }, [lockPublishedConfiguration, mountedColumnFilterList]);

  const contextValue: DatatableContextType<TData> = {
    table,
    columns,
    lock,
    state: localState,
    setState,
    isPaginationEnabled,
    rowCount: data.length,
    getConfigurationForColumn,
  };

  return <DatatableContext.Provider value={contextValue}>{children}</DatatableContext.Provider>;
};
